import { useState, useMemo, useEffect, useCallback } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import type { FilterState } from '@/utilities/types/shared-filter'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import debounce from 'lodash/debounce'

// Enable the required plugins
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

interface UseVirtualTableProps<T> {
  data: T[]
  filterState: FilterState
  containerRef: React.RefObject<HTMLDivElement>
  isProcessingTable?: boolean
  page: number
  setPage: React.Dispatch<React.SetStateAction<number>>
  rowsPerPage: number
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>
  totalElem: number
}

export const useAreaControllerVisualizedTable = <
  T extends Record<string, any>,
>({
  data,
  filterState,
  containerRef,
  isProcessingTable = false,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalElem,
}: UseVirtualTableProps<T>) => {
  // const sortedData = useMemo(() => {
  //   if (!filterState.sort) return data

  //   return [...data].sort((a, b) => {
  //     const { field, direction } = filterState.sort

  //     if (field === 'id') {
  //       const aNum = parseInt(String(a[field]), 10)
  //       const bNum = parseInt(String(b[field]), 10)
  //       return direction === 'asc' ? aNum - bNum : bNum - aNum
  //     }

  //     const aVal = String(a[field] || '').toLowerCase()
  //     const bVal = String(b[field] || '').toLowerCase()

  //     return direction === 'asc'
  //       ? aVal.localeCompare(bVal, undefined, { sensitivity: 'base' })
  //       : bVal.localeCompare(aVal, undefined, { sensitivity: 'base' })
  //   })
  // }, [data, filterState.sort])

  // Calculate total pages
  const totalPages = Math.round(totalElem / rowsPerPage)

  // Reset page when filters change or when current page would be out of bounds
  useEffect(() => {
    if (page > totalPages + 1) {
      setPage(0)
    }
  }, [data.length, page, rowsPerPage])

  // Calculate actual number of rows to display
  const actualRowCount = useMemo(
    () => Math.min(totalElem - (page - 1) * rowsPerPage, rowsPerPage),
    [totalElem, page, rowsPerPage],
  )

  const virtualizer = useVirtualizer({
    count: actualRowCount, // Use actualRowCount instead of rowsPerPage
    getScrollElement: () => containerRef.current,
    estimateSize: () => 52,
    overscan: 5,
  })

  const debouncedScroll = useMemo(
    () =>
      debounce(() => {
        virtualizer.measure()
      }, 16), // ~1 frame @60fps
    [virtualizer],
  )

  const handleVirtualizedScroll = useCallback(() => {
    debouncedScroll()
  }, [debouncedScroll])

  useEffect(() => {
    return () => {
      debouncedScroll.cancel()
    }
  }, [debouncedScroll])

  const handlePageChange = useCallback(
    (_: unknown, newPage: number) => {
      setPage(newPage)
      virtualizer.scrollToIndex(0)
    },
    [totalPages, virtualizer],
  )

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10))
      setPage(0) // Reset to page 0
      virtualizer.scrollToIndex(0)
    },
    [virtualizer],
  )

  const handleFirstPage = useCallback(() => {
    setPage(0)
  }, [])

  const handleLastPage = useCallback(() => {
    setPage(totalPages - 1)
  }, [totalPages])

  const handleSort = useCallback(
    (field: string) => {
      const newDirection =
        filterState.sort.field === field && filterState.sort.direction === 'asc'
          ? 'desc'
          : 'asc'
      return { field, direction: newDirection }
    },
    [filterState.sort],
  )

  return {
    virtualRows: virtualizer.getVirtualItems(),
    totalSize: actualRowCount * 52, // Use actualRowCount for total size
    paginatedItems: data,
    page,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    handleVirtualizedScroll,
    handleFirstPage,
    handleLastPage,
    virtualizer,
    // sortedData,
    handleSort,
  }
}
